import { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api from "../lib/api";
import { useCart } from "../context/CartContext";
import { CheckCircle2, Clock, XCircle } from "lucide-react";

const POLL_INTERVAL_MS = 2000;
const MAX_ATTEMPTS = 8;

export default function OrderSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "checking", payment_status: null, order: null });
  const attemptsRef = useRef(0);
  const { clear } = useCart();

  useEffect(() => {
    if (!sessionId) {
      setState({ status: "error", payment_status: null, order: null });
      return;
    }
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          setState({ status: "paid", payment_status: "paid", order: data.order });
          clear();
          return;
        }
        if (data.status === "expired") {
          setState({ status: "expired", payment_status: data.payment_status, order: data.order });
          return;
        }
        attemptsRef.current += 1;
        if (attemptsRef.current >= MAX_ATTEMPTS) {
          setState({ status: "timeout", payment_status: data.payment_status, order: data.order });
          return;
        }
        setTimeout(poll, POLL_INTERVAL_MS);
      } catch {
        setState({ status: "error", payment_status: null, order: null });
      }
    };
    poll();
    return () => {
      cancelled = true;
    };
  }, [sessionId, clear]);

  const renderContent = () => {
    if (state.status === "checking") {
      return (
        <>
          <Clock className="h-12 w-12 text-primary mx-auto mb-6 animate-pulse" />
          <h1 className="editorial-heading text-4xl md:text-5xl text-secondary mb-4">
            Confirming your payment…
          </h1>
          <p className="text-muted-foreground">Please don't close this page.</p>
        </>
      );
    }
    if (state.status === "paid") {
      return (
        <>
          <CheckCircle2 className="h-12 w-12 text-primary mx-auto mb-6" />
          <p className="overline text-primary mb-3">Payment received</p>
          <h1 className="editorial-heading text-4xl md:text-5xl text-secondary mb-4">
            Thank you for your order.
          </h1>
          <p className="text-muted-foreground max-w-md mx-auto mb-10">
            We've received your order and a confirmation has been sent to your email. Your
            shipment will be processed within one business day.
          </p>
          {state.order && (
            <div className="border border-border bg-card text-left max-w-md mx-auto p-6 mb-10" data-testid="order-summary">
              <p className="overline text-muted-foreground mb-3">Order #{state.order.id.slice(0, 8)}</p>
              <ul className="divide-y divide-border mb-4">
                {state.order.items.map((it) => (
                  <li key={it.product_id} className="flex justify-between py-3 text-sm">
                    <span className="text-secondary">{it.name} × {it.quantity}</span>
                    <span className="text-secondary">${(it.price * it.quantity).toFixed(2)}</span>
                  </li>
                ))}
              </ul>
              <div className="flex justify-between text-base pt-2 border-t border-border">
                <span className="text-secondary font-medium">Total</span>
                <span className="text-secondary font-medium">${state.order.total.toFixed(2)}</span>
              </div>
            </div>
          )}
          <Link
            to="/"
            data-testid="order-success-home"
            className="inline-flex items-center justify-center bg-secondary text-secondary-foreground px-8 py-4 uppercase tracking-[0.22em] text-xs hover:bg-primary hover:text-primary-foreground transition-colors"
          >
            Return home
          </Link>
        </>
      );
    }
    if (state.status === "expired") {
      return (
        <>
          <XCircle className="h-12 w-12 text-destructive mx-auto mb-6" />
          <h1 className="editorial-heading text-4xl md:text-5xl text-secondary mb-4">
            Session expired
          </h1>
          <p className="text-muted-foreground max-w-md mx-auto mb-10">
            Your checkout session has expired. Please try again from your cart.
          </p>
          <Link
            to="/checkout"
            className="inline-flex items-center justify-center bg-secondary text-secondary-foreground px-8 py-4 uppercase tracking-[0.22em] text-xs hover:bg-primary hover:text-primary-foreground transition-colors"
          >
            Return to checkout
          </Link>
        </>
      );
    }
    return (
      <>
        <Clock className="h-12 w-12 text-muted-foreground mx-auto mb-6" />
        <h1 className="editorial-heading text-4xl md:text-5xl text-secondary mb-4">
          Still processing
        </h1>
        <p className="text-muted-foreground max-w-md mx-auto mb-10">
          Your payment is taking longer than usual. You'll receive an email once it clears.
        </p>
        <Link
          to="/"
          className="inline-flex items-center justify-center bg-secondary text-secondary-foreground px-8 py-4 uppercase tracking-[0.22em] text-xs hover:bg-primary hover:text-primary-foreground transition-colors"
        >
          Return home
        </Link>
      </>
    );
  };

  return (
    <div className="px-6 py-32 text-center animate-in fade-in duration-500" data-testid="order-success-page">
      {renderContent()}
    </div>
  );
}
